import React from "react";
import { Play, Pause, ChevronUp, ChevronDown } from "lucide-react";
import { usePlayer } from "@/context/AudioContext";

function fmt(s) {
  if (!s || isNaN(s)) return "0:00";
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60);
  return `${m}:${sec.toString().padStart(2, "0")}`;
}

/**
 * Persistent, site-wide compact player. Fixed to the bottom, below content and
 * CTAs (main carries matching bottom padding) — it never covers navigation,
 * products or checkout controls on iPhone.
 */
export default function PlayerBar() {
  const p = usePlayer();
  if (!p || !p.track) return null;

  const t = p.track;

  return (
    <div data-testid="persistent-audio-player" className="fixed bottom-0 inset-x-0 z-40">
      {p.expanded && (
        <div
          data-testid="player-expanded"
          className="max-h-[65vh] overflow-y-auto bg-[#0F0F14]/98 backdrop-blur-xl border-t border-[#2A2A36]"
          role="region"
          aria-label="Music player expanded"
        >
          <div className="max-w-3xl mx-auto px-4 sm:px-6 py-6">
            <div className="flex items-center gap-5">
              <img src={t.cover} alt="" decoding="async" className="w-20 h-20 object-cover border border-[#2A2A36]" />
              <div className="min-w-0">
                <p className="font-display font-extrabold uppercase text-xl text-white truncate">{t.title}</p>
                <p className="font-mono text-xs tracking-[0.25em] text-[#F26522] mt-1">{t.artist}</p>
              </div>
            </div>

            <div className="mt-6">
              <p className="font-mono text-xs tracking-[0.25em] uppercase text-zinc-300 mb-3">Website exclusive — unreleased</p>
              <div className="flex items-center gap-4">
                <label htmlFor="player-volume" className="font-mono text-xs tracking-[0.25em] uppercase text-zinc-300 shrink-0">
                  Volume
                </label>
                <input
                  id="player-volume"
                  data-testid="player-volume"
                  type="range"
                  min="0"
                  max="1"
                  step="0.01"
                  value={p.volume}
                  onChange={(e) => p.changeVolume(Number(e.target.value))}
                  className="flex-1 accent-[#F26522] h-6"
                />
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="bg-[#0F0F14]/97 backdrop-blur-xl border-t border-[#2A2A36] pb-[env(safe-area-inset-bottom)]">
        <div className="h-0.5 bg-[#1A1A22]" aria-hidden="true">
          <div
            className="h-full bg-[#F26522] transition-[width] duration-300"
            style={{ width: p.duration ? `${(p.progress / p.duration) * 100}%` : "0%" }}
          />
        </div>
        <div className="max-w-7xl mx-auto px-3 sm:px-6 py-2.5 flex items-center gap-3 sm:gap-4">
          <img src={t.cover} alt="" decoding="async" className="w-12 h-12 sm:w-14 sm:h-14 object-cover border border-[#2A2A36] shrink-0" />
          <div className="min-w-0 flex-1">
            <p data-testid="player-track-title" className="font-display font-bold uppercase text-sm sm:text-base text-white truncate leading-tight">
              {t.title}
            </p>
            <p className="font-mono text-[11px] sm:text-xs tracking-[0.2em] text-[#F26522]">
              {t.artist} {p.duration ? `• ${fmt(p.progress)} / ${fmt(p.duration)}` : ""}
            </p>
          </div>

          <button
            data-testid="player-toggle"
            onClick={p.toggle}
            aria-label={p.playing ? "Pause" : "Play"}
            className={`min-w-[56px] h-[56px] rounded-full bg-[#F26522] hover:bg-[#FF7733] text-black flex items-center justify-center transition-transform duration-150 active:scale-90 ${
              p.playing ? "shadow-[0_0_28px_rgba(242,101,34,0.5)]" : "play-flare"
            }`}
          >
            {p.playing ? <Pause className="w-6 h-6" aria-hidden="true" /> : <Play className="w-6 h-6 ml-0.5" aria-hidden="true" />}
          </button>
          <button
            data-testid="player-expand"
            onClick={() => p.setExpanded(!p.expanded)}
            aria-label={p.expanded ? "Collapse player" : "Expand player"}
            aria-expanded={p.expanded}
            className="min-w-[52px] h-[52px] flex items-center justify-center text-white hover:text-[#F26522] transition-colors"
          >
            {p.expanded ? <ChevronDown className="w-6 h-6" aria-hidden="true" /> : <ChevronUp className="w-6 h-6" aria-hidden="true" />}
          </button>
        </div>
      </div>
    </div>
  );
}
